import { useEffect, useState } from "react";
import { useStore } from "../../store/hooks/use-store";

const intercomInnerHtmlScript = (appId: string) => `
(function () {
  var w = window;
  var ic = w.Intercom;
  if (typeof ic === "function") {
    ic("reattach_activator");
    ic("update", w.intercomSettings);
  } else {
    var d = document;
    var i = function () {
      i.c(arguments);
    };
    i.q = [];
    i.c = function (args) {
      i.q.push(args);
    };
    w.Intercom = i;
    var l = function () {
      var s = d.createElement("script");
      s.type = "text/javascript";
      s.async = true;
      s.src = "https://widget.intercom.io/widget/${appId}";
      var x = d.getElementsByTagName("script")[0];
      x.parentNode.insertBefore(s, x);
    };
    if (document.readyState === "complete") {
      l();
    } else if (w.attachEvent) {
      w.attachEvent("onload", l);
    } else {
      w.addEventListener("load", l, false);
    }
  }
})();
`;

const checkIfChatbotConsentIsGranted = (usercentricsServiceId: string): boolean => {
  return !!(window as any)?.UC_UI?.getServicesBaseInfo()?.find((service: any) => service?.id === usercentricsServiceId)
    ?.consent?.status;
};

export function useIntercomChatbot(
  appId: string | undefined,
  apiBase: string | undefined,
  usercentricsServiceId: string | undefined,
  isChatbotEnabled: boolean,
  languageIsoCode: string,
  countryIsoCode: string,
) {
  const { state } = useStore();
  const [consentGranted, setConsentGranted] = useState<boolean>(false);

  useEffect(() => {
    const checkIfChatbotConsentIsGrantedAndUpdateState = () => {
      const chatbotConsentGranted = usercentricsServiceId
        ? checkIfChatbotConsentIsGranted(usercentricsServiceId)
        : false;
      setConsentGranted(chatbotConsentGranted);
      return chatbotConsentGranted;
    };
    let intervalId: number | undefined = undefined;

    const chatbotConsentGranted = checkIfChatbotConsentIsGrantedAndUpdateState();
    if (!chatbotConsentGranted) {
      intervalId = window.setInterval(checkIfChatbotConsentIsGrantedAndUpdateState, 1000);
    }
    return () => {
      intervalId && clearInterval(intervalId);
    };
  }, [usercentricsServiceId]);

  useEffect(() => {
    const script = document.createElement("script");

    const stateAuth = state.auth;

    if (
      appId &&
      isChatbotEnabled &&
      consentGranted &&
      !(stateAuth?.user === "logged-in-user" && stateAuth?.userProfileSf.__tag === "loading")
    ) {
      const userIntercomSettings =
        stateAuth?.user === "logged-in-user" && stateAuth?.userProfileSf.__tag === "loaded"
          ? {
              name: stateAuth.userProfileSf.data.name,
              email: stateAuth.userProfileSf.data.email,
            }
          : {};

      (window as any).intercomSettings = {
        app_id: appId,
        api_base: apiBase,
        language_override: languageIsoCode.toLowerCase(),
        hide_default_launcher: false,
        "SWO Website Country": countryIsoCode
      };

      script.innerHTML = intercomInnerHtmlScript(appId);
      script.async = true;

      document.body.appendChild(script);
      try {
        // @ts-expect-error
        Intercom("boot", { app_id: appId, api_base: apiBase, ...userIntercomSettings });
      } catch (err) {
        console.log(err);
      }
    }
    return () => {
      try {
        //@ts-expect-error
        Intercom("hide");
        //@ts-expect-error
        Intercom("update", {
          hide_default_launcher: true,
        });
        document.body.removeChild(script);
        (window as any).intercomSettings = undefined;
        (window as any).Intercom = undefined;
      } catch {}
    };
  }, [
    isChatbotEnabled,
    languageIsoCode,
    countryIsoCode,
    appId,
    state.auth?.user,
    state.auth?.user === "logged-in-user" && state.auth?.userProfileSf.__tag,
    consentGranted,
  ]);
}
